/**
 * Bluetooth Service
 * Handles Web Bluetooth API connections for BLE scales and BLE label printers.
 * Auto-detects Android WebView environment and uses native bridge when available.
 *
 * Priority:
 *   1. Android WebView (window.AndroidBluetooth injected by native app)
 *   2. Web Bluetooth API (Chrome/Edge on HTTPS)
 */

import { androidBluetoothBridge } from './android-bluetooth.bridge';


// ─── Types ───────────────────────────────────────────────────────
export type DeviceType = 'scale' | 'printer';

export interface BtDeviceInfo {
    id: string;
    name: string;
    type: DeviceType;
    connected: boolean;
    rssi?: number;
    mac?: string;
    batteryLevel?: number;
}

export interface ScaleReading {
    weight: number;      // in kg
    unit: 'kg' | 'lb';
    stable: boolean;     // whether reading is settled
    timestamp: number;
}

// ─── Well-known BLE UUIDs ────────────────────────────────────────
// Many cheap BLE scales use a custom service. We accept all devices and
// try common patterns. Users can also pair by name prefix.
const SCALE_NAME_PREFIXES = ['S-Pro', 'HS-', 'XK', 'CAS', 'ACS', 'SF-', 'JCS', 'BSM', 'WH-', 'DY-'];
const PRINTER_NAME_PREFIXES = ['Zebra', 'XP-', 'xprinter', 'TSC', 'HPRT', 'GODEX', 'JP-', 'HM-', 'QR-', 'PT-', 'GP-', 'NB-', 'PeriPage', 'MUNBYN', 'Bluetooth Printer', 'D52', 'D35', 'D31', 'D45'];

// Common BLE service UUIDs for serial-over-BLE
const SERIAL_SERVICE_UUIDS = [
    '49535343-fe7d-4ae5-8fa9-9fafd205e455', // Microchip / common BLE serial
    '0000ff00-0000-1000-8000-00805f9b34fb', // Common Chinese BLE serial
    '0000ffe0-0000-1000-8000-00805f9b34fb', // Common Chinese BLE scale/serial
    'e7810a71-73ae-499d-8c15-faa9aef0c3f2', // RN4870/RN4871
    '0000ae30-0000-1000-8000-00805f9b34fb', // HPRT specific
    '0000ae3a-0000-1000-8000-00805f9b34fb', // HPRT write service
    '000018f0-0000-1000-8000-00805f9b34fb', // Generic label printer
    '00001800-0000-1000-8000-00805f9b34fb', // Generic Access
    '00001801-0000-1000-8000-00805f9b34fb', // Generic Attribute
];

const SERIAL_CHAR_UUIDS = [
    '49535343-8841-43f4-a8d4-ecbe34729bb3', // Microchip TX
    '49535343-1e4d-4bd9-ba61-23c647249616', // Microchip RX
    '0000ff02-0000-1000-8000-00805f9b34fb', // Common write
    '0000ff01-0000-1000-8000-00805f9b34fb', // Common notify/read
    '0000ffe1-0000-1000-8000-00805f9b34fb', // Common scale notify
    'bef8d6c9-9c21-4c9e-b632-bd58c1009f9f', // RN4870 TX
];

// ─── Bluetooth Service ───────────────────────────────────────────
class BluetoothService {
    private scaleDevice: BluetoothDevice | null = null;
    private printerDevice: BluetoothDevice | null = null;
    private scaleCharacteristic: BluetoothRemoteGATTCharacteristic | null = null;
    private printerWriteCharacteristic: BluetoothRemoteGATTCharacteristic | null = null;
    private isPrinting = false;
    private onWeightChange: ((reading: ScaleReading) => void) | null = null;
    private onDisconnect: ((type: DeviceType) => void) | null = null;
    // Android native bridge device IDs and names
    private androidScaleDeviceId: string | null = null;
    private androidPrinterDeviceId: string | null = null;
    private androidScaleDeviceName: string | null = null;
    private androidPrinterDeviceName: string | null = null;
    // Device picker: UI registers this to show a selection modal for Android Bridge scan results
    private devicePicker: ((devices: BtDeviceInfo[]) => Promise<BtDeviceInfo | null>) | null = null;
    // Scan progress: UI registers this to get real-time device updates during scanning
    private scanProgressCallback: ((device: BtDeviceInfo) => void) | null = null;

    /** Disconnect all currently connected devices (used for manual reset) */
    disconnectAll(): void {
        try {
            if (this.scaleDevice || this.androidScaleDeviceId) {
                this.disconnectScale();
            }
            if (this.printerDevice || this.androidPrinterDeviceId) {
                this.disconnectPrinter();
            }
        } catch (err) {
            console.error('[BluetoothService] Cleanup error:', err);
        }
    }

    /** Register a device picker callback (called when Android Bridge scan returns multiple results) */
    setDevicePicker(picker: (devices: BtDeviceInfo[]) => Promise<BtDeviceInfo | null>): void {
        this.devicePicker = picker;
    }

    /** Register a scan progress callback (called for each new device found during scanning) */
    setScanProgressCallback(cb: ((device: BtDeviceInfo) => void) | null): void {
        this.scanProgressCallback = cb;
    }

    /** Helper: scan via Android Bridge then prompt user to pick (or auto-pick if only 1 result) */
    private async androidScanAndPick(filter: 'scale' | 'printer' | 'all'): Promise<BtDeviceInfo> {
        const devices = await androidBluetoothBridge.scanDevices(
            filter,
            this.scanProgressCallback ?? undefined   // <-- stream each device to UI in real time
        );
        if (!devices.length) throw new Error(`未找到蓝牙设备，请确认设备已开启`);
        if (devices.length === 1) return devices[0];
        if (this.devicePicker) {
            const picked = await this.devicePicker(devices);
            if (!picked) throw new Error('未选择设备');
            return picked;
        }
        // No picker registered — return first
        return devices[0];
    }


    /** Check if any Bluetooth backend is available */
    isSupported(): boolean {
        // Priority 1: Android WebView native bridge
        if (androidBluetoothBridge.isAvailable()) return true;
        // Priority 2: Web Bluetooth API (HTTPS required)
        if (typeof navigator === 'undefined') return false;
        if (!window.isSecureContext) return false;
        if (!('bluetooth' in navigator)) return false;
        return true;
    }

    /** Returns a human-readable reason why Bluetooth is not supported */
    getUnsupportedReason(): string {
        if (androidBluetoothBridge.isAvailable()) return ''; // Should not be called if supported

        const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
        const isAndroid = /Android/i.test(ua);
        const isWebView = isAndroid && /wv|WebView/i.test(ua);
        const isChrome = /Chrome\//i.test(ua) && !/wv/i.test(ua);

        if (typeof navigator === 'undefined') return '运行环境不支持';

        if (!window.isSecureContext) {
            return '需要 HTTPS 安全连接才能使用蓝牙功能。请确认应用通过 HTTPS 或 localhost 访问。';
        }
        if (!('bluetooth' in navigator)) {
            if (isWebView) {
                return 'Android 系统 WebView 不支持蓝牙功能。请在 App 中集成蓝牙原生接口（Android Bridge），或用 Chrome 浏览器打开。';
            }
            if (isAndroid && !isChrome) {
                return '当前浏览器不支持 Web Bluetooth。请使用 Chrome 浏览器打开本应用。';
            }
            return '当前浏览器不支持 Web Bluetooth API。请使用 Chrome 或 Edge 浏览器。';
        }
        return '蓝牙不可用，原因未知。请检查系统蓝牙权限。';
    }

    /** Returns true if running in Android WebView with native bridge */
    isAndroidBridge(): boolean {
        return androidBluetoothBridge.isAvailable();
    }

    /** Scan and connect to a BLE scale */
    async connectScale(): Promise<BtDeviceInfo> {
        // ─── Android WebView Bridge Path ───────────────────────────
        if (androidBluetoothBridge.isAvailable()) {
            const device = await this.androidScanAndPick('scale');
            const info = await androidBluetoothBridge.connectDevice(device.id, 'scale');
            androidBluetoothBridge.startWeightStream(device.id, (reading) => {
                this.onWeightChange?.(reading);
            });
            this.androidScaleDeviceId = device.id;
            this.androidScaleDeviceName = info.name;
            return info;
        }

        // ─── Web Bluetooth API Path ─────────────────────────────────
        if (!this.isSupported()) throw new Error('您的浏览器不支持蓝牙功能');

        try {
            const device = await navigator.bluetooth.requestDevice({
                filters: SCALE_NAME_PREFIXES.map(prefix => ({ namePrefix: prefix })),
                optionalServices: SERIAL_SERVICE_UUIDS,
            });
            return await this.connectToScale(device);
        } catch (err: any) {
            if (err.name === 'NotFoundError') throw new Error('未选择设备');
            throw err;
        }
    }

    /** Scan and connect with accept-all (fallback) */
    async connectScaleAll(): Promise<BtDeviceInfo> {
        if (androidBluetoothBridge.isAvailable()) {
            const device = await this.androidScanAndPick('all');
            const info = await androidBluetoothBridge.connectDevice(device.id, 'scale');
            androidBluetoothBridge.startWeightStream(device.id, (reading) => {
                this.onWeightChange?.(reading);
            });
            this.androidScaleDeviceId = device.id;
            this.androidScaleDeviceName = info.name;
            return info;
        }

        if (!this.isSupported()) throw new Error('您的浏览器不支持蓝牙功能');
        const device = await navigator.bluetooth.requestDevice({
            acceptAllDevices: true,
            optionalServices: SERIAL_SERVICE_UUIDS,
        });
        return await this.connectToScale(device);
    }

    private async connectToScale(device: BluetoothDevice): Promise<BtDeviceInfo> {
        if (!device.gatt) throw new Error('设备不支持 GATT');

        // Disconnect previous
        if (this.scaleDevice?.gatt?.connected) {
            this.scaleDevice.gatt.disconnect();
        }

        const server = await device.gatt.connect();
        this.scaleDevice = device;

        // Listen for disconnection
        device.addEventListener('gattserverdisconnected', () => {
            this.scaleCharacteristic = null;
            this.onDisconnect?.('scale');
        });

        // Try to find a notification characteristic for weight data
        await this.subscribeToScaleNotifications(server);

        return {
            id: device.id,
            name: device.name || '未知秤',
            type: 'scale',
            connected: true,
        };
    }

    private async subscribeToScaleNotifications(server: BluetoothRemoteGATTServer) {
        for (const svcUuid of SERIAL_SERVICE_UUIDS) {
            try {
                const service = await server.getPrimaryService(svcUuid);
                const chars = await service.getCharacteristics();

                for (const char of chars) {
                    if (char.properties.notify || char.properties.indicate) {
                        this.scaleCharacteristic = char;
                        await char.startNotifications();
                        char.addEventListener('characteristicvaluechanged', (event: any) => {
                            const value = event.target.value as DataView;
                            const reading = this.parseScaleData(value);
                            if (reading && this.onWeightChange) {
                                this.onWeightChange(reading);
                            }
                        });
                        console.log('[BT Scale] Subscribed to notifications on:', svcUuid, char.uuid);
                        return;
                    }
                }
            } catch {
                // Service not found, try next
            }
        }
        console.warn('[BT Scale] No notification characteristic found — weight data may not stream');
    }

    /** Parse weight data from common BLE scale protocols */
    private parseScaleData(data: DataView): ScaleReading | null {
        try {
            // Many cheap scales send ASCII strings like "ST,+12.50,kg"
            const decoder = new TextDecoder();
            const str = decoder.decode(data.buffer);

            // Pattern 1: "ST,+12.50,kg" or "US,+12.50,kg"
            const match = str.match(/(ST|US|GS),\s*([+-]?\d+\.?\d*)\s*(kg|lb|g)/i);
            if (match) {
                let weight = parseFloat(match[2]);
                const unit = match[3].toLowerCase();
                if (unit === 'g') weight = weight / 1000;
                return {
                    weight: Math.abs(weight),
                    unit: unit === 'lb' ? 'lb' : 'kg',
                    stable: match[1] === 'ST',
                    timestamp: Date.now(),
                };
            }

            // Pattern 2: Raw hex weight (2 bytes, little-endian, divide by 100)
            if (data.byteLength >= 2 && data.byteLength <= 8) {
                const raw = data.getUint16(data.byteLength - 2, true);
                if (raw > 0 && raw < 100000) {
                    return {
                        weight: raw / 100,
                        unit: 'kg',
                        stable: true,
                        timestamp: Date.now(),
                    };
                }
            }
        } catch {
            // Ignore parse errors
        }
        return null;
    }

    /** Scan and connect to a BLE printer */
    async connectPrinter(): Promise<BtDeviceInfo> {
        // ─── Android Bridge Path ────────────────────────────────────
        if (androidBluetoothBridge.isAvailable()) {
            const device = await this.androidScanAndPick('printer');
            const info = await androidBluetoothBridge.connectDevice(device.id, 'printer');
            this.androidPrinterDeviceId = device.id;
            this.androidPrinterDeviceName = info.name;
            return info;
        }

        // ─── Web Bluetooth Path ─────────────────────────────────────
        if (!this.isSupported()) throw new Error('您的浏览器不支持蓝牙功能');
        try {
            const device = await navigator.bluetooth.requestDevice({
                filters: PRINTER_NAME_PREFIXES.map(prefix => ({ namePrefix: prefix })),
                optionalServices: SERIAL_SERVICE_UUIDS,
            });
            return await this.connectToPrinter(device);
        } catch (err: any) {
            if (err.name === 'NotFoundError') throw new Error('未选择设备');
            throw err;
        }
    }

    /** Scan printer with accept-all — Android shows picker for all devices */
    async connectPrinterAll(): Promise<BtDeviceInfo> {
        if (androidBluetoothBridge.isAvailable()) {
            const device = await this.androidScanAndPick('all');
            const info = await androidBluetoothBridge.connectDevice(device.id, 'printer');
            this.androidPrinterDeviceId = device.id;
            this.androidPrinterDeviceName = info.name;
            return info;
        }

        if (!this.isSupported()) throw new Error('您的浏览器不支持蓝牙功能');
        const device = await navigator.bluetooth.requestDevice({
            acceptAllDevices: true,
            optionalServices: SERIAL_SERVICE_UUIDS,
        });
        return await this.connectToPrinter(device);
    }

    private async connectToPrinter(device: BluetoothDevice): Promise<BtDeviceInfo> {
        if (!device.gatt) throw new Error('设备不支持 GATT');

        if (this.printerDevice?.gatt?.connected) {
            this.printerDevice.gatt.disconnect();
        }

        const server = await device.gatt.connect();
        this.printerDevice = device;

        device.addEventListener('gattserverdisconnected', () => {
            this.printerWriteCharacteristic = null;
            this.onDisconnect?.('printer');
        });

        // Find writable characteristic for sending print data
        await this.findPrinterWriteChar(server);

        if (!this.printerWriteCharacteristic) {
            device.gatt.disconnect();
            throw new Error('连接成功但未找到打印机写入通道');
        }

        return {
            id: device.id,
            name: device.name || '未知打印机',
            type: 'printer',
            connected: true,
        };
    }

    // Preferred write characteristic UUIDs — ff02 is the ESC/POS print channel for most Chinese label printers
    private static readonly PREFERRED_WRITE_CHAR_UUIDS = [
        '0000ff02-0000-1000-8000-00805f9b34fb', // Common Chinese BLE print channel (ESC/POS)
        '49535343-8841-43f4-a8d4-ecbe34729bb3', // Microchip TBUART serial
        'bef8d6c9-9c21-4c9e-b632-bd58c1009f9f', // RN4870 TX (phone→device)
    ];

    private async findPrinterWriteChar(server: BluetoothRemoteGATTServer) {
        // ═══ Step 1: Discover ALL services and log everything ═══
        const allWritableChars: { svc: string; char: BluetoothRemoteGATTCharacteristic; preferred: boolean }[] = [];

        try {
            console.log('[BT Printer] ═══ Discovering ALL services on device ═══');
            const allServices = await server.getPrimaryServices();
            console.log(`[BT Printer] Found ${allServices.length} services total`);

            for (const service of allServices) {
                try {
                    const chars = await service.getCharacteristics();
                    console.log(`[BT Printer] Service [${service.uuid}] — ${chars.length} characteristics:`);
                    for (const c of chars) {
                        const props: string[] = [];
                        if (c.properties.write) props.push('write');
                        if (c.properties.writeWithoutResponse) props.push('writeNoResp');
                        if (c.properties.notify) props.push('notify');
                        if (c.properties.read) props.push('read');
                        if (c.properties.indicate) props.push('indicate');
                        console.log(`  ├─ [${c.uuid}] ${props.join(', ')}`);

                        if (c.properties.write || c.properties.writeWithoutResponse) {
                            const isPreferred = BluetoothService.PREFERRED_WRITE_CHAR_UUIDS.includes(c.uuid);
                            allWritableChars.push({ svc: service.uuid, char: c, preferred: isPreferred });
                        }
                    }
                } catch (err: any) {
                    console.log(`  └─ (error reading chars: ${err.message})`);
                }
            }
        } catch (err: any) {
            console.warn('[BT Printer] getPrimaryServices() failed:', err.message);
        }

        // ═══ Step 2: Also try known service UUIDs individually (needed for some BLE stacks) ═══
        for (const svcUuid of SERIAL_SERVICE_UUIDS) {
            try {
                const service = await server.getPrimaryService(svcUuid);
                const chars = await service.getCharacteristics();
                for (const c of chars) {
                    if (c.properties.write || c.properties.writeWithoutResponse) {
                        const alreadyFound = allWritableChars.some(e => e.char.uuid === c.uuid && e.svc === svcUuid);
                        if (!alreadyFound) {
                            const isPreferred = BluetoothService.PREFERRED_WRITE_CHAR_UUIDS.includes(c.uuid);
                            allWritableChars.push({ svc: svcUuid, char: c, preferred: isPreferred });
                        }
                    }
                }
            } catch {
                // Service not found on this device, skip
            }
        }

        console.log(`[BT Printer] Total writable characteristics found: ${allWritableChars.length}`);
        for (const entry of allWritableChars) {
            console.log(`  ${entry.preferred ? '★' : '○'} [${entry.svc}] → [${entry.char.uuid}]`);
        }

        // ═══ Step 3: Select the best characteristic ═══
        // When multiple preferred chars exist, pick the one with highest priority
        // (earliest in PREFERRED_WRITE_CHAR_UUIDS list)
        const preferredChars = allWritableChars
            .filter(e => e.preferred)
            .sort((a, b) => {
                const idxA = BluetoothService.PREFERRED_WRITE_CHAR_UUIDS.indexOf(a.char.uuid);
                const idxB = BluetoothService.PREFERRED_WRITE_CHAR_UUIDS.indexOf(b.char.uuid);
                return idxA - idxB;
            });
        const preferred = preferredChars[0] || null;
        const writeOnly = allWritableChars.find(e => !e.char.properties.notify);
        const selected = preferred || writeOnly || allWritableChars[0];

        if (selected) {
            this.printerWriteCharacteristic = selected.char;
            const label = preferred ? 'PREFERRED' : writeOnly ? 'WRITE-ONLY' : 'FALLBACK';
            console.log(`[BT Printer] ✅ Selected (${label}): service=${selected.svc} char=${selected.char.uuid}`);
        } else {
            console.warn('[BT Printer] ❌ No writable characteristic found on this device!');
        }
    }

    /** Send raw bytes to printer (ESC/POS or CPCL/TSPL) */
    async printRaw(data: Uint8Array): Promise<void> {
        if (this.isPrinting) {
            throw new Error('正在打印中，请等待上一任务完成');
        }

        this.isPrinting = true;

        try {
            console.log(`[BT Printer] Starting Job: ${data.length} bytes`);

            // Debug: log first 50 bytes as hex to verify data content
            const preview = Array.from(data.slice(0, 50)).map(b => b.toString(16).padStart(2, '0')).join(' ');
            console.log(`[BT Printer] Data preview (hex head): ${preview}`);
            // Log TAIL of data (critical: must contain FORM + PRINT for CPCL)
            const tailStart = Math.max(0, data.length - 50);
            const tailPreview = Array.from(data.slice(tailStart)).map(b => b.toString(16).padStart(2, '0')).join(' ');
            console.log(`[BT Printer] Data preview (hex tail): ${tailPreview}`);
            // Also log as text for CPCL/TSPL debugging
            try {
                const textHead = new TextDecoder('utf-8', { fatal: false }).decode(data.slice(0, 120));
                const textTail = new TextDecoder('utf-8', { fatal: false }).decode(data.slice(tailStart));
                console.log(`[BT Printer] Data text head: ${textHead.replace(/\r\n/g, '\\r\\n')}`);
                console.log(`[BT Printer] Data text tail: ${textTail.replace(/\r\n/g, '\\r\\n')}`);
            } catch { }

            // ─── Android Bridge Path ────────────────────────────────────
            if (androidBluetoothBridge.isAvailable() && this.androidPrinterDeviceId) {
                console.log(`[AndroidBT Printer] Sending ${data.length} bytes via native bridge (single call)...`);
                // ⚠️ CRITICAL: Send entire payload in ONE native call.
                // Native BleManager.writePrinter() already handles BLE chunking (100 bytes/chunk).
                // Previously we split here into 512-byte chunks and made multiple writePrinter calls,
                // but this caused concurrent coroutine races on the native side — multiple chunks
                // would write to BLE simultaneously, corrupting the command stream.
                // The printer would receive garbled data and ignore it (no paper feed).
                await androidBluetoothBridge.writePrinter(this.androidPrinterDeviceId, data);
                console.log('[AndroidBT Printer] Job complete');
                return;
            }

            // ─── Web Bluetooth Path ─────────────────────────────────────
            if (!this.printerWriteCharacteristic) {
                throw new Error('打印机未连接或未找到写入通道');
            }

            // Chunk size 100: proven balance between throughput and compatibility
            const CHUNK_SIZE = 100;
            let lastProgress = 0;

            for (let i = 0; i < data.length; i += CHUNK_SIZE) {
                const chunk = data.slice(i, i + CHUNK_SIZE);
                let writeSuccess = false;
                let retries = 3;

                while (!writeSuccess && retries > 0) {
                    try {
                        if (!this.printerWriteCharacteristic) {
                            throw new Error('设备在打印过程中断开连接');
                        }

                        const chunkBuffer = chunk.buffer.slice(chunk.byteOffset, chunk.byteOffset + chunk.byteLength);

                        if (this.printerWriteCharacteristic.properties.writeWithoutResponse) {
                            await this.printerWriteCharacteristic.writeValueWithoutResponse(chunkBuffer);
                        } else if (this.printerWriteCharacteristic.properties.write) {
                            await this.printerWriteCharacteristic.writeValueWithResponse(chunkBuffer);
                        } else {
                            throw new Error('该通道不支持写入');
                        }

                        await new Promise(r => setTimeout(r, 30));
                        writeSuccess = true;
                    } catch (err: any) {
                        retries--;
                        console.warn(`[BT Printer] Chunk failed at ${i}, retries left: ${retries}, Error: ${err.message}`);
                        if (retries === 0 || err.message.includes('断开连接')) throw err;
                        await new Promise(r => setTimeout(r, 200));
                    }
                }

                const progress = Math.floor((i / data.length) * 100);
                if (progress >= lastProgress + 20) {
                    console.log(`[BT Printer] Progress: ${progress}%`);
                    lastProgress = progress;
                }
            }
            console.log('[BT Printer] Job complete');
        } catch (err: any) {
            const errorMsg = err.message || String(err);
            console.error('[BT Printer] Print error:', errorMsg);
            throw new Error(errorMsg);
        } finally {
            this.isPrinting = false;
        }
    }

    /** Send text string to printer as ESC/POS */
    async printText(text: string): Promise<void> {
        const encoder = new TextEncoder();
        const data = encoder.encode(text);
        // For label printers: no extra feed or cut command.
        // Just a single line feed to flush the print buffer.
        const footer = new Uint8Array([
            0x0A,                                // Single LF to flush
        ]);
        const combined = new Uint8Array([...data, ...footer]);
        await this.printRaw(combined);
    }

    /** Disconnect scale */
    disconnectScale() {
        // Android Bridge
        if (androidBluetoothBridge.isAvailable() && this.androidScaleDeviceId) {
            androidBluetoothBridge.stopWeightStream(this.androidScaleDeviceId);
            androidBluetoothBridge.disconnectDevice(this.androidScaleDeviceId);
            this.androidScaleDeviceId = null;
            this.androidScaleDeviceName = null;
            return;
        }
        // Web Bluetooth
        if (this.scaleDevice?.gatt?.connected) {
            this.scaleDevice.gatt.disconnect();
        }
        this.scaleDevice = null;
        this.scaleCharacteristic = null;
    }

    /** Disconnect printer */
    disconnectPrinter() {
        // Android Bridge
        if (androidBluetoothBridge.isAvailable() && this.androidPrinterDeviceId) {
            androidBluetoothBridge.disconnectDevice(this.androidPrinterDeviceId);
            this.androidPrinterDeviceId = null;
            this.androidPrinterDeviceName = null;
            return;
        }
        // Web Bluetooth
        if (this.printerDevice?.gatt?.connected) {
            this.printerDevice.gatt.disconnect();
        }
        this.printerDevice = null;
        this.printerWriteCharacteristic = null;
    }

    /** Check if scale is connected */
    isScaleConnected(): boolean {
        if (androidBluetoothBridge.isAvailable()) return !!this.androidScaleDeviceId;
        return this.scaleDevice?.gatt?.connected ?? false;
    }

    /** Check if printer is connected */
    isPrinterConnected(): boolean {
        if (androidBluetoothBridge.isAvailable()) return !!this.androidPrinterDeviceId;
        return this.printerDevice?.gatt?.connected ?? false;
    }

    /** Register weight change callback */
    setOnWeightChange(cb: (reading: ScaleReading) => void) {
        this.onWeightChange = cb;
    }

    /** Register disconnect callback */
    setOnDisconnect(cb: (type: DeviceType) => void) {
        this.onDisconnect = cb;
    }

    /** Get connected scale device info */
    getScaleDevice(): BtDeviceInfo | null {
        if (androidBluetoothBridge.isAvailable() && this.androidScaleDeviceId) {
            return {
                id: this.androidScaleDeviceId,
                name: this.androidScaleDeviceName || '蓝牙秤 (Android)',
                type: 'scale',
                connected: true,
            };
        }
        if (!this.scaleDevice) return null;
        return {
            id: this.scaleDevice.id,
            name: this.scaleDevice.name || '未知秤',
            type: 'scale',
            connected: this.scaleDevice.gatt?.connected ?? false,
        };
    }

    /** Get connected printer device info */
    getPrinterDevice(): BtDeviceInfo | null {
        if (androidBluetoothBridge.isAvailable() && this.androidPrinterDeviceId) {
            return {
                id: this.androidPrinterDeviceId,
                name: this.androidPrinterDeviceName || '蓝牙打印机 (Android)',
                type: 'printer',
                connected: true,
            };
        }
        if (!this.printerDevice) return null;
        return {
            id: this.printerDevice.id,
            name: this.printerDevice.name || '未知打印机',
            type: 'printer',
            connected: this.printerDevice.gatt?.connected ?? false,
        };
    }

    /**
     * Try to reconnect to a previously paired device.
     * Uses `getDevices()` to find devices the browser remembers,
     * then `watchAdvertisements()` to wait for the device to become available.
     */
    async tryAutoConnect(type: DeviceType, deviceName?: string): Promise<BtDeviceInfo | null> {
        if (!this.isSupported()) {
            console.log(`[BT ${type}] Auto-connect: Web Bluetooth not supported`);
            return null;
        }
        if (!(navigator.bluetooth as any).getDevices) {
            console.log(`[BT ${type}] Auto-connect: getDevices() API not available`);
            return null;
        }

        try {
            // Step 1: Get previously paired devices
            const devices: BluetoothDevice[] = await (navigator.bluetooth as any).getDevices();
            console.log(`[BT ${type}] getDevices() returned ${devices.length} device(s):`,
                devices.map(d => d.name || d.id).join(', ') || '(empty)');

            // Step 2: Find matching device
            const prefixes = type === 'scale' ? SCALE_NAME_PREFIXES : PRINTER_NAME_PREFIXES;
            const target = devices.find((d: BluetoothDevice) => {
                if (deviceName) return d.name === deviceName;
                return prefixes.some(p => d.name?.startsWith(p));
            });

            if (!target) {
                console.log(`[BT ${type}] No matching device for "${deviceName}" in paired list`);
                return null;
            }

            console.log(`[BT ${type}] Matched device: ${target.name} (id: ${target.id})`);

            // Step 3: Wait for device advertisement (makes it connectable)
            if ((target as any).watchAdvertisements) {
                console.log(`[BT ${type}] Calling watchAdvertisements...`);
                try {
                    const adPromise = new Promise<void>((resolve) => {
                        target.addEventListener('advertisementreceived', () => resolve(), { once: true });
                    });
                    await (target as any).watchAdvertisements();
                    // Wait up to 5 seconds for an advertisement
                    await Promise.race([
                        adPromise,
                        new Promise(r => setTimeout(r, 5000)),
                    ]);
                    console.log(`[BT ${type}] Advertisement phase done`);
                } catch (e: any) {
                    console.log(`[BT ${type}] watchAdvertisements issue (non-fatal): ${e.message}`);
                }
            }

            // Step 4: Connect
            console.log(`[BT ${type}] Attempting gatt.connect()...`);
            if (type === 'scale') return await this.connectToScale(target);
            else return await this.connectToPrinter(target);
        } catch (err: any) {
            console.warn(`[BT ${type}] Auto-connect failed at:`, err.message || err);
        }
        return null;
    }
}

// Singleton
export const bluetoothService = new BluetoothService();
